import { useLocation, useNavigate } from "react-router-dom";
import { useGetProposedChallengeDetails, useProposedChallengeMutation } from "../../queries/challenge";
import Loader from "../../components/Common/Loader";
import Chip from "../../components/Chip";
import { ActionButton } from "../../components/Forms/ActionButton";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from "../Submissions/components/Modal";
import { useState } from "react";
import RichTextEditor from "../../components/Forms/RichTextEditor";
import EditIcon from '../../utils/media/icons/edit_icon.png';

const ProposedChallengeDetails = () => {

    const { state: { proposedChallengeId } } = useLocation();
    const navigate = useNavigate();

    const { data, isPending, isError } = useGetProposedChallengeDetails(proposedChallengeId);

    const { mutate: mutateChallenge, isLoading: isMutating } = useProposedChallengeMutation("open for voting");

    const [rejectionModelOpen, setRejectionModelOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState(data?.rejectionReason || "");

    const handleAccept = (challengeId) => {
        mutateChallenge(({ challengeId: challengeId, status: "open for voting" }), {
            onSuccess: () => {
                toast.success("Challenge proposal status updated!");
                setTimeout(() => {
                    navigate("/challenges/proposed-challenges");
                }, 2000);
            },
            onError: (error) => {
                toast.error("Failed to change the challenge proposal status.");
                console.error(error);
            },
        });
    }

    const handleReject = (challengeId) => {
        mutateChallenge(({ challengeId: challengeId, status: "rejected", rejectionReason }), {
            onSuccess: () => {
                toast.success("Challenge proposal rejected successfully!");
                setTimeout(() => {
                    navigate("/challenges/proposed-challenges");
                }, 2000);
            },
            onError: (error) => {
                toast.error("Failed to reject the challenge proposal.");
                console.error(error);
            },
        });
    };

    const handleCreateChallenge = () => {
        navigate("/challenges/challenges-main/create-challenge", {
            state: {
                challengeName: data.name,
                challengeDescription: data?.description,
                challengeInspireMe: data?.inspireMe,
                challengeProposedByUser: data?.proposedByUser,
                challengeCategory: data?.category,
                proposedChallengeId: data?._id,
                challengeCoverImage: data?.sampleMedia[0],
            }
        })
    }

    const renderChipStatus = () => {
        if (data?.acceptanceStatus === 'live in app') {
            return <Chip value={data?.acceptanceStatus} variant={'filled'} color="green" />;
        } else if (data?.acceptanceStatus === 'rejected') {
            return <Chip value={data?.acceptanceStatus} variant={'filled'} color="red" />;
        } else if (data?.acceptanceStatus === 'pending') {
            return <Chip value={data?.acceptanceStatus} variant={'filled'} color="yellow" />;
        } else if (data?.acceptanceStatus === 'open for voting') {
            return <Chip value={data?.acceptanceStatus} variant={'filled'} color="purple" />;
        } else if (data?.acceptanceStatus === 'voting over') {
            return <Chip value={data?.acceptanceStatus} variant={'filled'} color="magenta" />;
        } else {
            return <Chip value={data?.acceptanceStatus} variant={'filled'} color="blue" />;
        }
    }

    if (isPending) {
        return <Loader />;
    }

    if (isError) {
        return <div className="text-center mt-10">Error loading proposed challenge details.</div>;
    }

    return (
        <div className="flex flex-col text-base font-semibold text-gray-900 rounded ">
            <div className="bg-white rounded shadow-[0px_0px_25px_rgba(94,92,154,0.1)] p-5">
                <div className="flex justify-between items-center">
                    <h1
                        className="gap-2.5 w-fit text-xl font-bold max-md:max-w-full text-primary capitalize cursor-pointer"
                    >
                        Edit Proposed Challenge
                    </h1>
                    <div className="flex items-center justify-between gap-3">
                        {renderChipStatus()}
                        <button
                            className="rounded p-1.5 bg-[rgba(0,122,155,0.1)] hover:bg-[rgba(0,122,155,0.2)] min-w-5 relative group"
                            onClick={() => navigate('/challenges/proposed-challenges-edit', { state: { proposedChallengeId: data?._id } })}
                        >
                            <img src={EditIcon} alt="edit" className="w-4" />
                            <span className="absolute top-full left-1/2 transform -translate-x-1/2 mb-1 hidden group-hover:block bg-[rgba(0,122,155,0.5)] text-white text-xs rounded py-1 px-2">
                                Edit Proposal
                            </span>
                        </button>
                    </div>
                </div>
                <div className="flex flex-col gap-5 mt-5">
                    <div className="flex justify-between">
                        <div className="flex flex-col gap-5">
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Challenge Name: </h2>
                                <p>{data?.name}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Proposed By: </h2>
                                <p>{data?.proposedByUser?.userName || 'Unknown'}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Category: </h2>
                                <p>{data?.category?.name || 'Unknown'}</p>
                                <img src={data?.category?.logo} className="size-5" />
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Description: </h2>
                                <p>{data?.description}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Inspire Me: </h2>
                                <p>{data?.inspireMe}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Proposed Date: </h2>
                                <p>{new Date(data?.createdAt).toLocaleString('en-US', { day: '2-digit', month: 'long', year: 'numeric', hour: "2-digit", minute: '2-digit', hour12: true })}</p>
                            </div>
                            {
                                (data?.acceptanceStatus === 'open for voting' || data?.acceptanceStatus === 'voting over') && (
                                    <div className="flex flex-row gap-2">
                                        <h2 className="text-lg font-semibold text-primary">Voting Start Date: </h2>
                                        <p>{new Date(data?.challengeCreation?.votingStartDate).toLocaleString('en-US', { day: '2-digit', month: 'long', year: 'numeric', hour: "2-digit", minute: '2-digit', hour12: true })}</p>
                                    </div>
                                )
                            }
                            {
                                (data?.acceptanceStatus === 'open for voting' || data?.acceptanceStatus === 'voting over') && (
                                    <div className="flex flex-row gap-2">
                                        <h2 className="text-lg font-semibold text-primary">Voting End Date: </h2>
                                        <p>{new Date(data?.challengeCreation?.canCreationDate).toLocaleString('en-US', { day: '2-digit', month: 'long', year: 'numeric', hour: "2-digit", minute: '2-digit', hour12: true })}</p>
                                    </div>
                                )
                            }
                            {
                                (data?.acceptanceStatus === 'open for voting' || data?.acceptanceStatus === 'voting over') && (
                                    <div className="flex flex-row gap-2">
                                        <h2 className="text-lg font-semibold text-primary">Upvotes: </h2>
                                        <p>{data?.upvotes.length}</p>
                                    </div>
                                )
                            }
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Challenge Created: </h2>
                                <p>{data?.challengeCreation?.created ? "Yes" : "No"}</p>
                            </div>
                            {
                                data?.challengeCreation?.challengeId &&
                                <div className="flex flex-row gap-2">
                                    <h2 className="text-lg font-semibold text-primary">Challenge Name: </h2>
                                    <p className="cursor-pointer" onClick={() => navigate('/challenges/challenges-main/edit-challenge', { state: { _id: data?.challengeCreation?.challengeId?._id } })}>{data?.challengeCreation?.challengeId?.name}</p>
                                </div>
                            }
                            {
                                data?.challengeCreation?.challengeId &&
                                <div className="flex flex-row gap-2">
                                    <h2 className="text-lg font-semibold text-primary">Challenge Active: </h2>
                                    <p className="cursor-pointer" onClick={() => navigate('/challenges/challenges-main/edit-challenge', { state: { _id: data?.challengeCreation?.challengeId?._id } })}>{data?.challengeCreation?.challengeId?.active ? "Active" : "Inactive"}</p>
                                </div>
                            }
                        </div>
                        <img src={data?.sampleMedia[0]} alt="user uploaded sample media" className="w-80 h-full" />
                    </div>
                </div>
                <div className="flex flex-row justify-between gap-5 mt-5">
                    <div className="flex flex-row gap-3">
                        {
                            // data?.challengeCreation?.canCreationDate &&
                            // new Date() >= new Date(data?.challengeCreation?.canCreationDate) &&
                            (data?.acceptanceStatus === 'open for voting' || data?.acceptanceStatus === 'voting over')
                            && (
                                <ActionButton
                                    variant={"cancel"}
                                    text={"Create Challenge"}
                                    handleSubmit={handleCreateChallenge}
                                />
                            )
                        }
                    </div>
                    <div className="flex flex-row gap-3">
                        {
                            !data?.challengeCreation?.challengeId?.active && (
                                <ActionButton
                                    variant={"cancel"}
                                    text={"Reject"}
                                    handleSubmit={() => setRejectionModelOpen(true)}
                                    disabled={isMutating}
                                />
                            )
                        }
                        {
                            data?.acceptanceStatus === 'pending' && (
                                <ActionButton
                                    variant={"submit"}
                                    text={"Open For Voting"}
                                    handleSubmit={() => handleAccept(data?._id)}
                                    disabled={isMutating}
                                />
                            )
                        }
                    </div>
                </div>
                {/* Rejection Modal */}
                <Modal isOpen={rejectionModelOpen} onClose={() => setRejectionModelOpen(false)} className="max-h-96 overflow-y-auto">
                    <div className="flex flex-col gap-5">
                        <h1 className="text-lg font-semibold text-primary">Are you sure you want to reject this challenge?</h1>
                        <RichTextEditor
                            label={'Reason for rejection'}
                            placeholder={'Please provide a reason for rejection'}
                            value={rejectionReason}
                            onChange={setRejectionReason}
                            required={true}
                        />
                        <div className="flex flex-row justify-end gap-5">
                            <ActionButton
                                variant={"cancel"}
                                text={"Cancel"}
                                handleSubmit={() => setRejectionModelOpen(false)}
                            />
                            <ActionButton
                                variant={"submit"}
                                text={"Reject"}
                                disabled={isMutating}
                                handleSubmit={() => {
                                    handleReject(data?._id);
                                    setRejectionModelOpen(false);
                                }}
                            />
                        </div>
                    </div>
                </Modal>
            </div>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default ProposedChallengeDetails;